/**
 * Warm the code for the pages people open most, while the browser is idle.
 *
 * Every route is a lazy chunk (App.tsx), which is right for first load, but it
 * means the FIRST tap on Schedules / Messages / Tasks in a session waits on a
 * chunk download and parse before the page can even start fetching its data —
 * Schedules alone is ~90 KB gzipped. Nothing about that wait is useful: by the
 * time someone is looking at the dashboard, the network is quiet.
 *
 * So once the app is interactive, fetch those chunks one at a time in idle
 * slots. `import()` of the same specifier resolves to the same module, so the
 * later `lazy()` render finds it already loaded and the page appears at once.
 *
 * Deliberately conservative:
 *  - one chunk per idle slot, never a burst that competes with the page's own
 *    data requests;
 *  - skipped entirely under Save-Data or on 2G, where downloading pages the
 *    user may never open costs them real money and bandwidth;
 *  - failures are swallowed — a prefetch that fails just means the page loads
 *    the way it always did (chunk-reload.ts still handles a stale deploy).
 */

export type ChunkLoader = () => Promise<unknown>;

interface ConnectionLike {
  saveData?: boolean;
  effectiveType?: string;
}

export function shouldPrefetchRoutes(connection: ConnectionLike | undefined | null): boolean {
  if (!connection) return true;
  if (connection.saveData) return false;
  return connection.effectiveType !== '2g' && connection.effectiveType !== 'slow-2g';
}

type IdleScheduler = (cb: () => void) => () => void;

/** requestIdleCallback where it exists (not Safari), else a short timeout. */
export const defaultIdle: IdleScheduler = (cb) => {
  const w = typeof window !== 'undefined' ? (window as Window & typeof globalThis) : undefined;
  if (w && typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(cb, { timeout: 4000 });
    return () => w.cancelIdleCallback(id);
  }
  const t = setTimeout(cb, 1500);
  return () => clearTimeout(t);
};

/**
 * Load `loaders` one after another, each in its own idle slot. Returns a
 * cancel function (for unmount / sign-out) that stops any not yet started.
 */
export function prefetchRoutesWhenIdle(
  loaders: ChunkLoader[],
  idle: IdleScheduler = defaultIdle,
  connection: ConnectionLike | undefined | null =
    typeof navigator !== 'undefined' ? (navigator as Navigator & { connection?: ConnectionLike }).connection : undefined,
): () => void {
  if (!shouldPrefetchRoutes(connection) || loaders.length === 0) return () => {};
  let cancelled = false;
  let cancelSlot: () => void = () => {};
  let i = 0;

  const next = () => {
    if (cancelled || i >= loaders.length) return;
    cancelSlot = idle(() => {
      if (cancelled) return;
      const load = loaders[i++];
      Promise.resolve()
        .then(load)
        .catch(() => {
          /* the page will load normally on demand */
        })
        .finally(next);
    });
  };
  next();

  return () => {
    cancelled = true;
    cancelSlot();
  };
}
